import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Save } from "lucide-react";
import AdminAdventure from "@/pages/AdminAdventure";
import AdminCommerce from "@/pages/AdminCommerce";
import AdminGameCards from "@/pages/AdminGameCards";
import { BookPdfPanel } from "@/components/BookPdfPanel";
import AdminOrders from "@/pages/AdminOrders";
import AdminCeda from "@/pages/AdminCeda";
import AdminReviews from "@/pages/AdminReviews";

const CATEGORIES = ["CORPS", "CŒUR", "CERVEAU", "CONSCIENCE", "ACTION", "INTÉGRATION"];

const FIELDS = [
  { key: "title", label: "Titre", type: "text" },
  { key: "function", label: "Fonction", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "cover", label: "Couverture (URL)", type: "text" },
  { key: "amazon_url", label: "Lien Amazon — livre broché (bouton « Acheter sur Amazon »)", type: "text" },
  { key: "kindle_url", label: "Lien Amazon — version Kindle / e-book", type: "text" },
  { key: "amazon_price", label: "Prix affiché du livre broché Amazon (ex : 19,90 €)", type: "text" },
  { key: "pdf_url", label: "PDF", type: "text" },
  { key: "ebook_url", label: "Ebook", type: "text" },
  { key: "other_url", label: "Autre", type: "text" },
];

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("tao_admin_token"));
  const [password, setPassword] = useState("");
  const [books, setBooks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [daysModal, setDaysModal] = useState(null);
  const [days, setDays] = useState([]);
  const [demoV, setDemoV] = useState(0);
  const [tab, setTab] = useState("carnets");
  const [pdfCarnets, setPdfCarnets] = useState([]);
  const loadPdf = () => api.get("/admin/carnets", { headers: { Authorization: `Bearer ${sessionStorage.getItem("tao_admin_token")}` } }).then((r) => setPdfCarnets(r.data || [])).catch(() => {});

  useEffect(() => {
    if (authed) { api.get("/books").then((r) => setBooks(r.data)); loadPdf(); }
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/login", { password });
      sessionStorage.setItem("tao_admin_token", r.data.token);
      setAuthed(true);
      toast.success("Connecté");
    } catch {
      toast.error("Mot de passe invalide");
    }
  };

  const pw = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("tao_admin_token")}` } });

  const uploadDemo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const b64 = await new Promise((res) => {
      const r = new FileReader();
      r.onloadend = () => res(r.result.split(",")[1]);
      r.readAsDataURL(file);
    });
    try {
      await api.put("/admin/settings/demo-jour1", { mime: file.type || "audio/mpeg", audio: b64 }, pw());
      setDemoV(Date.now());
      toast.success("Exemple audio mis à jour");
    } catch {
      toast.error("Échec de l'envoi");
    }
  };

  const toggleActive = async (book) => {
    const r = await api.put(`/admin/books/${book.id}`, { active: !book.active }, pw());
    setBooks((bs) => bs.map((b) => (b.id === book.id ? r.data : b)));
    toast.success(r.data.active ? "Carnet activé" : "Carnet désactivé");
  };

  const saveEdit = async () => {
    const r = await api.put(`/admin/books/${editing.id}`, editing, pw());
    setBooks((bs) => bs.map((b) => (b.id === editing.id ? r.data : b)));
    setEditing(null);
    toast.success("Carnet enregistré");
  };

  const openDays = async (book) => {
    const r = await api.get(`/books/${book.id}/days`);
    setDays(r.data);
    setDaysModal(book);
  };

  const genDays = async () => {
    const r = await api.post(`/admin/books/${daysModal.id}/init-days`, {}, pw());
    setDays(r.data);
    toast.success("21 jours générés");
  };

  const updateDayField = (id, field, val) =>
    setDays((ds) => ds.map((d) => (d.id === id ? { ...d, [field]: val } : d)));

  const saveDay = async (day) => {
    const r = await api.put(
      `/admin/days/${day.id}`,
      {
        title: day.title || "",
        category: day.category || "",
        question: day.question || "",
        exercise: day.exercise || "",
        action: day.action || "",
      },
      pw()
    );
    setDays((ds) => ds.map((d) => (d.id === day.id ? r.data : d)));
    toast.success(`Jour ${day.day_number} enregistré`);
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24">
        <div className="rounded-2xl bg-white border border-[#E8DCC4] p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#C9A24B]/15 text-[#C9A24B] flex items-center justify-center">
            <Lock size={22} />
          </div>
          <h1 className="font-serif text-2xl text-[#1F2A24] mt-4">Administration</h1>
          <p className="font-sans text-sm text-[#1F2A24]/60 mt-1">Accès réservé à l'administrateur</p>
          <form onSubmit={login} className="mt-6">
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe"
              className="w-full rounded-full border border-[#E8DCC4] px-5 py-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
            />
            <button
              type="submit"
              data-testid="admin-login-button"
              className="w-full mt-3 rounded-full bg-[#385E3A] text-white py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"
            >
              SE CONNECTER
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-3xl text-[#1F2A24]">Administration des carnets</h1>
        <button
          data-testid="admin-logout"
          onClick={() => { sessionStorage.removeItem("tao_admin_token"); setAuthed(false); }}
          className="font-sans text-sm text-[#1F2A24]/60 hover:text-[#A63B36]"
        >
          Déconnexion
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-tabs">
        <button
          data-testid="admin-tab-carnets"
          onClick={() => setTab("carnets")}
          className={`rounded-full px-5 py-2 font-ui text-sm transition-colors ${tab === "carnets" ? "bg-[#385E3A] text-white" : "border border-[#E8DCC4] text-[#1F2A24]/70"}`}
        >
          Carnets 21 jours
        </button>
        <button
          data-testid="admin-tab-aventure"
          onClick={() => setTab("aventure")}
          className={`rounded-full px-5 py-2 font-ui text-sm transition-colors ${tab === "aventure" ? "bg-[#385E3A] text-white" : "border border-[#E8DCC4] text-[#1F2A24]/70"}`}
        >
          Aventures interactives
        </button>
        <button
          data-testid="admin-tab-commerce"
          onClick={() => setTab("commerce")}
          className={`rounded-full px-5 py-2 font-ui text-sm transition-colors ${tab === "commerce" ? "bg-[#385E3A] text-white" : "border border-[#E8DCC4] text-[#1F2A24]/70"}`}
        >
          Produits & Offres
        </button>
        <button
          data-testid="admin-tab-jeu"
          onClick={() => setTab("jeu")}
          className={`rounded-full px-5 py-2 font-ui text-sm transition-colors ${tab === "jeu" ? "bg-[#385E3A] text-white" : "border border-[#E8DCC4] text-[#1F2A24]/70"}`}
        >
          Cartes du Jeu
        </button>
        <button
          data-testid="admin-tab-orders"
          onClick={() => setTab("orders")}
          className={`rounded-full px-5 py-2 font-ui text-sm transition-colors ${tab === "orders" ? "bg-[#385E3A] text-white" : "border border-[#E8DCC4] text-[#1F2A24]/70"}`}
        >
          Commandes
        </button>
        <button
          data-testid="admin-tab-ceda"
          onClick={() => setTab("ceda")}
          className={`rounded-full px-5 py-2 font-ui text-sm transition-colors ${tab === "ceda" ? "bg-[#385E3A] text-white" : "border border-[#E8DCC4] text-[#1F2A24]/70"}`}
        >
          CEDA
        </button>
        <button
          data-testid="admin-tab-reviews"
          onClick={() => setTab("reviews")}
          className={`rounded-full px-5 py-2 font-ui text-sm transition-colors ${tab === "reviews" ? "bg-[#385E3A] text-white" : "border border-[#E8DCC4] text-[#1F2A24]/70"}`}
        >
          Avis lecteurs
        </button>
      </div>

      {tab === "aventure" && <div className="mt-8"><AdminAdventure /></div>}
      {tab === "commerce" && <div className="mt-8"><AdminCommerce /></div>}
      {tab === "jeu" && <div className="mt-8"><AdminGameCards /></div>}
      {tab === "orders" && <div className="mt-8"><AdminOrders /></div>}
      {tab === "ceda" && <div className="mt-8"><AdminCeda /></div>}
      {tab === "reviews" && <div className="mt-8"><AdminReviews /></div>}

      {tab === "carnets" && (<>
      <div className="mt-6 rounded-xl bg-white border border-[#E8DCC4] p-4" data-testid="admin-demo-card">
        <p className="font-serif text-lg text-[#1F2A24]">Exemple audio du Jour 1</p>
        <p className="font-sans text-sm text-[#1F2A24]/60 mt-1">
          Remplacez l'exemple vocal proposé au Jour 1 (fichier mp3 ou webm).
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <input data-testid="admin-demo-file" type="file" accept="audio/*" onChange={uploadDemo} className="font-sans text-sm" />
          <audio data-testid="admin-demo-preview" src={`${API}/settings/demo-jour1?t=${demoV}`} controls className="h-9" />
        </div>
      </div>

      <div className="mt-8 space-y-3" data-testid="admin-book-list">
        {books.map((b) => (
          <div key={b.id} className="rounded-xl bg-white border border-[#E8DCC4] p-4 flex flex-wrap items-center gap-4">
            <span className="font-serif text-xl text-[#385E3A] w-8">{b.number}</span>
            <div className="flex-1 min-w-[180px]">
              <p className="font-serif text-lg text-[#1F2A24]">{b.title}</p>
              <p className="font-ui text-[11px] tracking-widest text-[#C9A24B]">{b.function}</p>
            </div>
            <span
              data-testid={`admin-status-${b.number}`}
              className={`rounded-full px-3 py-1 font-ui text-[11px] ${
                b.active ? "bg-[#385E3A]/15 text-[#385E3A]" : "bg-[#E8DCC4] text-[#1F2A24]/50"
              }`}
            >
              {b.active ? "ACTIF" : "INACTIF"}
            </span>
            <button
              data-testid={`admin-toggle-${b.number}`}
              onClick={() => toggleActive(b)}
              className="rounded-full border border-[#C9A24B] text-[#1F2A24] px-4 py-2 font-ui text-xs hover:bg-[#C9A24B]/10 transition-colors"
            >
              {b.active ? "DÉSACTIVER" : "ACTIVER"}
            </button>
            <button
              data-testid={`admin-edit-${b.number}`}
              onClick={() => setEditing({ ...b })}
              className="rounded-full bg-[#1F2A24] text-white px-4 py-2 font-ui text-xs hover:bg-black transition-colors"
            >
              MODIFIER
            </button>
            {b.number > 0 && (
              <button
                data-testid={`admin-days-${b.number}`}
                onClick={() => openDays(b)}
                className="rounded-full border border-[#385E3A] text-[#385E3A] px-4 py-2 font-ui text-xs hover:bg-[#385E3A]/10 transition-colors"
              >
                MODIFIER LES JOURS
              </button>
            )}
            <BookPdfPanel book={b} carnet={pdfCarnets.find((c) => c.book_id === b.id)} onChange={loadPdf} onBookChange={(nb) => setBooks((bs) => bs.map((x) => (x.id === nb.id ? nb : x)))} />
          </div>
        ))}
      </div>
      </>)}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div
            data-testid="admin-edit-modal"
            className="bg-white rounded-3xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-serif text-2xl text-[#1F2A24]">Carnet {editing.number}</h2>
            <div className="mt-5 space-y-4">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="font-sans text-xs uppercase tracking-wider text-[#1F2A24]/60">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      data-testid={`admin-field-${f.key}`}
                      rows={3}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="w-full mt-1 rounded-xl border border-[#E8DCC4] p-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
                    />
                  ) : (
                    <input
                      data-testid={`admin-field-${f.key}`}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="w-full mt-1 rounded-full border border-[#E8DCC4] px-4 py-2.5 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
                    />
                  )}
                </div>
              ))}
              <label className="flex items-center gap-2 font-sans text-sm text-[#1F2A24]">
                <input
                  type="checkbox"
                  data-testid="admin-field-active"
                  checked={!!editing.active}
                  onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                />
                Carnet actif
              </label>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="rounded-full px-5 py-2.5 font-ui text-sm text-[#1F2A24]/70">
                Annuler
              </button>
              <button
                data-testid="admin-save-button"
                onClick={saveEdit}
                className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-2.5 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"
              >
                <Save size={16} /> ENREGISTRER
              </button>
            </div>
          </div>
        </div>
      )}

      {daysModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDaysModal(null)}>
          <div
            data-testid="admin-days-modal"
            className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-[#1F2A24]">Jours — {daysModal.title}</h2>
              <button onClick={() => setDaysModal(null)} className="font-sans text-sm text-[#1F2A24]/60 hover:text-[#A63B36]">
                Fermer
              </button>
            </div>

            {days.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-sans text-sm text-[#1F2A24]/70">Aucun jour n'existe encore pour ce carnet.</p>
                <button
                  data-testid="admin-generate-days"
                  onClick={genDays}
                  className="mt-4 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"
                >
                  GÉNÉRER 21 JOURS
                </button>
              </div>
            ) : (
              <div className="mt-5 space-y-2">
                {days.map((d) => (
                  <details key={d.id} data-testid={`admin-day-${d.day_number}`} className="rounded-xl border border-[#E8DCC4] p-3">
                    <summary className="font-serif text-[#1F2A24] cursor-pointer">
                      Jour {d.day_number} — {d.title || "(sans titre)"}
                    </summary>
                    <div className="mt-3 space-y-2">
                      <input
                        data-testid={`admin-day-title-${d.day_number}`}
                        value={d.title || ""}
                        onChange={(e) => updateDayField(d.id, "title", e.target.value)}
                        placeholder="Titre du jour"
                        className="w-full rounded-full border border-[#E8DCC4] px-4 py-2 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
                      />
                      <select
                        data-testid={`admin-day-category-${d.day_number}`}
                        value={d.category || ""}
                        onChange={(e) => updateDayField(d.id, "category", e.target.value)}
                        className="w-full rounded-full border border-[#E8DCC4] px-4 py-2 font-sans text-sm bg-white"
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <textarea
                        rows={2}
                        value={d.question || ""}
                        onChange={(e) => updateDayField(d.id, "question", e.target.value)}
                        placeholder="Question"
                        className="w-full rounded-xl border border-[#E8DCC4] p-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
                      />
                      <textarea
                        rows={2}
                        value={d.exercise || ""}
                        onChange={(e) => updateDayField(d.id, "exercise", e.target.value)}
                        placeholder="Expérience"
                        className="w-full rounded-xl border border-[#E8DCC4] p-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
                      />
                      <textarea
                        rows={2}
                        value={d.action || ""}
                        onChange={(e) => updateDayField(d.id, "action", e.target.value)}
                        placeholder="Action"
                        className="w-full rounded-xl border border-[#E8DCC4] p-3 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A24B]/40"
                      />
                      <button
                        data-testid={`admin-day-save-${d.day_number}`}
                        onClick={() => saveDay(d)}
                        className="rounded-full bg-[#385E3A] text-white px-5 py-2 font-ui text-sm hover:bg-[#2c4a2e] transition-colors"
                      >
                        ENREGISTRER LE JOUR
                      </button>
                    </div>
                  </details>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
